function isValidSignature(view: DataView, signature: string) {
    for (let i = 0; i < signature.length; i++) {
        if (view.getUint8(i) !== signature.charCodeAt(i)) return false;
    }

    return true;
}

function concatXmlParts(parts: Uint8Array[]): Uint8Array {
    const totalLen = parts.reduce((sum, p) => sum + p.length, 0);
    const result = new Uint8Array(totalLen);
    let offset = 0;
    for (const p of parts) {
        result.set(p, offset);
        offset += p.length;
    }
    return result;
}

function extractXmlBounds(xml: string) {
    const doc = new DOMParser().parseFromString(xml, "text/xml");
    const blocks = doc.getElementsByTagName("cartesianBounds");
    if (!blocks.length) return null;

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    for (const block of blocks) {
        const get = (tag: string) => {
            const el = block.getElementsByTagName(tag)[0];
            if (!el) return null;
            const v = parseFloat(el.textContent ?? "");
            return Number.isFinite(v) ? v : null;
        };
        const x0 = get("xMinimum");
        const x1 = get("xMaximum");
        const y0 = get("yMinimum");
        const y1 = get("yMaximum");

        if (x0 !== null && x1 !== null && y0 !== null && y1 !== null) {
            minX = Math.min(minX, x0);
            maxX = Math.max(maxX, x1);
            minY = Math.min(minY, y0);
            maxY = Math.max(maxY, y1);
        }
    }

    const bounds = { minX, maxX, minY, maxY };
    if (Object.values(bounds).some((v) => !Number.isFinite(v))) return null;

    return bounds;
}

// Offsets from: https://community.asprs.org/wg-las/viewdocument/las-test
async function parseLasBounds(file: File) {
    const sliceSize = Math.min(file.size, 65536);
    if (sliceSize < 227) return null;

    const buffer = await file.slice(0, sliceSize).arrayBuffer();
    const view = new DataView(buffer);
    if (!isValidSignature(view, "LASF")) return null;

    const maxX = view.getFloat64(179, true);
    const minX = view.getFloat64(187, true);
    const maxY = view.getFloat64(195, true);
    const minY = view.getFloat64(203, true);
    const maxZ = view.getFloat64(211, true);
    const minZ = view.getFloat64(219, true);

    const bounds = { minX, maxX, minY, maxY, minZ, maxZ };
    if (Object.values(bounds).some((v) => !Number.isFinite(v))) return null;

    return bounds;
}

async function parseE57Bounds(file: File) {
    if (file.size < 48) return null;

    const headerBuf = await file.slice(0, 48).arrayBuffer();
    const headerView = new DataView(headerBuf);
    if (!isValidSignature(headerView, "ASTM-E57")) return null;

    const xmlPhysicalOffset = Number(headerView.getBigUint64(24, true));
    const xmlPhysicalLength = Number(headerView.getBigUint64(32, true));
    const pageSize = Number(headerView.getBigUint64(40, true));

    if (xmlPhysicalOffset + xmlPhysicalLength > file.size) return null;

    const xmlParts: Uint8Array[] = [];
    let logicalRemaining = xmlPhysicalLength;
    let filePos = xmlPhysicalOffset;
    while (logicalRemaining > 0) {
        const offsetInPage = filePos % pageSize;
        const usableEnd = pageSize - 4;
        const availableInPage = usableEnd - offsetInPage;
        const chunkLen = Math.min(availableInPage, logicalRemaining);
        const buf = await file.slice(filePos, filePos + chunkLen).arrayBuffer();
        xmlParts.push(new Uint8Array(buf));

        logicalRemaining -= chunkLen;
        filePos += chunkLen + (offsetInPage + chunkLen === usableEnd ? 4 : 0);
    }

    const xmlString = new TextDecoder().decode(concatXmlParts(xmlParts));

    return extractXmlBounds(xmlString);
}

export async function parseBounds(file: File) {
    const type = file.name.split(".").pop();
    if (type === "e57") {
        return parseE57Bounds(file);
    } else {
        return parseLasBounds(file);
    }
}
